/**
 * Vocabolario unico delle schermate e-commerce (08 sez. 2, 15 sez. 4.3). Modulo puro, nessun import React.
 * Tutte le schermate leggono etichette, colori e frasi da qui: la stessa cosa ha sempre lo stesso nome.
 * Mai "sync" o "deal" nei testi visibili: si dice aggiornamento, allineamento, Offerta.
 */
import { parseSyncState, storeProblems } from './syncState';
import { formatCountIt, formatDateIt, formatDurationIt, relativeTimeIt } from './timeFormat';
import type { CommerceProvider, JobKind, JobStatus, StoreStatus } from './types';

export type Tone = 'green' | 'amber' | 'red' | 'blue' | 'gray';
/** Livelli di evidenza di 15 sez. 1: 0 silenzioso ... 4 bloccante. */
export type EvidenceLevel = 0 | 1 | 2 | 3 | 4;

export const PROVIDER_LABEL: Readonly<Record<CommerceProvider, string>> = Object.freeze({
  shopify: 'Shopify',
  woocommerce: 'WooCommerce',
});

interface StatusDef { label: string; tone: Tone; pulse: boolean; level: EvidenceLevel }

export const STORE_STATUS: Readonly<Record<StoreStatus, StatusDef>> = Object.freeze({
  analyzing: { label: 'Analisi in corso', tone: 'blue', pulse: true, level: 1 },
  awaiting_confirmation: { label: 'Aspetta la tua conferma', tone: 'amber', pulse: false, level: 3 },
  syncing: { label: 'Allineamento in corso', tone: 'blue', pulse: true, level: 1 },
  connected: { label: 'Attivo', tone: 'green', pulse: false, level: 0 },
  paused: { label: 'In pausa', tone: 'gray', pulse: false, level: 1 },
  reauth_required: { label: 'Da ricollegare', tone: 'red', pulse: false, level: 4 },
  suspended_billing: { label: 'Sospeso: addon non attivo', tone: 'red', pulse: false, level: 4 },
  disconnected: { label: 'Scollegato', tone: 'gray', pulse: false, level: 0 },
});

/** Motivi delle pause automatiche (lifecycleRules.ts PAUSE_TEXT del core, in italiano semplice). */
/**
 * Perche' un negozio va ricollegato, in una frase: permessi nuovi da accettare (sync_state.last_error
 * 'scopes_missing', es. dopo un aggiornamento dell'app) oppure accesso rifiutato dal negozio.
 */
export function reauthReason(syncState: unknown, platform: string): string {
  return parseSyncState(syncState).lastError?.code === 'scopes_missing'
    ? `${platform} chiede di accettare dei permessi nuovi per Flusia`
    : `${platform} non accetta più l'accesso di Flusia`;
}

export const PAUSE_REASON_TEXT: Readonly<Record<string, string>> = Object.freeze({
  store_down: 'Il negozio non risponde da un po\': abbiamo fermato gli aggiornamenti e riproviamo da soli.',
  circuit: 'Troppi errori di collegamento di fila: abbiamo fermato gli aggiornamenti per non fare danni.',
  billing: 'L\'addon Magazzino + E-commerce non è attivo.',
  global: 'Gli aggiornamenti verso i negozi sono fermi per manutenzione.',
});

export interface StoreLike {
  provider: CommerceProvider;
  status: StoreStatus;
  sync_state?: unknown;
  last_synced_at?: string | null;
  updated_at?: string | null;
  disconnected_at?: string | null;
}

export interface StoreStatusView extends StatusDef {
  /** Anello rosso sul pallino verde: attivo ma con errori (08 sez. 2.1). */
  ring: boolean;
  sentence: string;
}

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

/** Stato del negozio con gli stati calcolati sopra ad "Attivo" (fermo per un freno, errori). */
export function storeStatusView(s: StoreLike, now: number = Date.now()): StoreStatusView {
  const def = STORE_STATUS[s.status] ?? STORE_STATUS.connected;
  const state = parseSyncState(s.sync_state);
  const platform = PROVIDER_LABEL[s.provider] ?? 'Il negozio';
  const lastUpdate = relativeTimeIt(state.updatedAt ?? s.last_synced_at ?? s.updated_at ?? null, now);
  const base = (sentence: string, over: Partial<StoreStatusView> = {}): StoreStatusView =>
    ({ ...def, ring: false, sentence, ...over });

  switch (s.status) {
    case 'analyzing':
      return base('Stiamo leggendo il negozio. Non cambia niente finché non confermi.');
    case 'awaiting_confirmation':
      return base('Il collegamento è pronto: niente è stato ancora scritto finché non confermi.');
    case 'syncing':
      return base('Stiamo portando i dati. Puoi continuare a lavorare.');
    case 'paused': {
      const reason = state.pause?.reason ?? 'manual';
      if (reason !== 'manual' && PAUSE_REASON_TEXT[reason]) return base(PAUSE_REASON_TEXT[reason], { level: 4, tone: 'red' });
      return base(`In pausa da ${relativeTimeIt(state.pause?.since ?? s.updated_at ?? null, now)}. Gli aggiornamenti riprendono quando lo riattivi.`);
    }
    case 'reauth_required':
      return base(`${reauthReason(s.sync_state, platform)}. Ricollegalo per riprendere.`);
    case 'suspended_billing':
      return base('L\'addon Magazzino + E-commerce non è attivo: gli aggiornamenti sono fermi. Riattivalo per riprendere.');
    case 'disconnected':
      return base(`Scollegato il ${formatDateIt(s.disconnected_at ?? s.updated_at ?? null)}. I dati già portati restano in Flusia.`);
    default: {
      const p = storeProblems(state);
      if (p.held > 0) {
        return base('Un aggiornamento si è fermato per un controllo e aspetta una decisione.', { label: 'Fermo per un controllo', tone: 'amber', level: 3 });
      }
      if (p.errors > 0) {
        return base(`Ultimo aggiornamento ${lastUpdate}.`, { label: `Attivo, con ${p.errors} ${plural(p.errors, 'errore', 'errori')}`, ring: true, level: 2 });
      }
      return base(`Ultimo aggiornamento ${lastUpdate}.`);
    }
  }
}

/**
 * Ultimo errore di un lavoro (product_sync_jobs.errors, scritto dal runner a ogni fetta fallita): e' il motivo
 * di "Interrotto, riprende da solo" e di "Non riuscito". null se non c'e' o il lavoro e' chiuso bene.
 */
export function lastJobError(job: { status: string; errors?: unknown }): { at: string | null; message: string } | null {
  if (job.status === 'completed' || job.status === 'cancelled' || !Array.isArray(job.errors)) return null;
  for (let i = job.errors.length - 1; i >= 0; i--) {
    const e = job.errors[i] as { at?: unknown; message?: unknown } | null;
    if (e && typeof e.message === 'string' && e.message.trim()) {
      return { at: typeof e.at === 'string' ? e.at : null, message: e.message.trim() };
    }
  }
  return null;
}

/** Stato di un elemento in un negozio (08 sez. 2.2), per la fase 2 (Prodotti nei negozi). */
export const ITEM_SYNC_STATUS: Readonly<Record<string, { label: string; tone: Tone; hint: string }>> = Object.freeze({
  synced: { label: 'Allineato', tone: 'green', hint: 'Uguale in Flusia e nel negozio.' },
  pending: { label: 'In coda', tone: 'blue', hint: 'La modifica partirà a breve.' },
  error: { label: 'Errore', tone: 'red', hint: 'Non riuscito dopo i tentativi. Premi Riprova.' },
  conflict: { label: 'Da decidere', tone: 'amber', hint: 'Flusia e il negozio hanno cambiato lo stesso dato. Scegli tu quale tenere.' },
  ambiguous: { label: 'Abbinamento incerto', tone: 'amber', hint: 'Ci sono più elementi che potrebbero corrispondere. Scegli tu.' },
  archived: { label: 'Archiviato', tone: 'gray', hint: 'Tolto da una parte, archiviato dall\'altra. Niente è stato cancellato.' },
  orphan: { label: 'Non più nel negozio', tone: 'gray', hint: 'Nel negozio non c\'è più. In Flusia resta.' },
});

export const JOB_KIND_LABEL: Readonly<Record<JobKind, string>> = Object.freeze({
  connect_check: 'Controllo del collegamento',
  analysis: 'Analisi',
  preview: 'Anteprima',
  initial_import: 'Collegamento',
  catalog_apply: 'Aggiornamento catalogo',
  stock_apply: 'Aggiornamento giacenze',
  reconcile: 'Verifica allineamento',
  reconcile_nightly: 'Controllo notturno',
  backfill_orders: 'Import ordini',
  snapshot: 'Istantanea',
  restore: 'Ripristino',
  undo_job: 'Annullamento di un lavoro',
  export: 'Esportazione dei dati',
});

export const jobKindLabel = (k: string | null | undefined): string =>
  (k && (JOB_KIND_LABEL as Record<string, string>)[k]) || 'Lavoro';

export interface JobLike {
  status: JobStatus;
  next_attempt_at: string | null;
  checkpoint: Record<string, unknown> | null;
  error_count: number | null;
}

/** Stato di un lavoro in chiaro (08 sez. 2.3). `live` = contatori dal vivo di sync_state.job. */
export function jobStatusView(
  j: JobLike,
  live: { done: number; total: number | null } | null,
  now: number = Date.now(),
): { label: string; tone: Tone } {
  const errors = j.error_count ?? 0;
  switch (j.status) {
    case 'pending': return { label: 'In attesa di partire', tone: 'gray' };
    case 'held': return { label: 'Fermo per un controllo', tone: 'amber' };
    case 'paused': return { label: 'In pausa', tone: 'gray' };
    case 'cancelled': return { label: 'Annullato', tone: 'gray' };
    case 'failed': return { label: 'Non riuscito', tone: 'red' };
    case 'completed':
      return errors > 0
        ? { label: `Completato, ${formatCountIt(errors)} non riusciti`, tone: 'amber' }
        : { label: 'Completato', tone: 'green' };
    default: {
      const next = j.next_attempt_at ? Date.parse(j.next_attempt_at) : NaN;
      if (Number.isFinite(next) && next > now) {
        return { label: `Interrotto, riprende da solo tra ${formatDurationIt(next - now)}`, tone: 'amber' };
      }
      const trial = j.checkpoint && typeof j.checkpoint.trial === 'object' && j.checkpoint.trial !== null
        ? (j.checkpoint.trial as { done?: unknown }) : null;
      if (trial && trial.done !== true) return { label: 'Blocco di prova in corso', tone: 'blue' };
      if (live && live.total !== null && live.total > 0) {
        return { label: `In corso, ${formatCountIt(live.done)} di ${formatCountIt(live.total)}`, tone: 'blue' };
      }
      return { label: 'In corso', tone: 'blue' };
    }
  }
}

/** Nome da mostrare: display_name, altrimenti l'indirizzo senza protocollo. */
export function storeDisplayName(s: { display_name: string | null; shop_domain: string }): string {
  const n = s.display_name?.trim();
  return n && n.length > 0 ? n : s.shop_domain.replace(/^https?:\/\//, '');
}

/** Indirizzo apribile del negozio (Shopify: dominio myshopify; WooCommerce: URL del sito). */
export function storeUrl(s: { provider: CommerceProvider; shop_domain: string }): string {
  return /^https:\/\//.test(s.shop_domain) ? s.shop_domain : `https://${s.shop_domain}`;
}

/** Cosa puo' fare Flusia, in chiaro (08 sez. 5.2), dagli scope concessi da Shopify. */
export const SCOPE_TEXT: Readonly<Record<string, string>> = Object.freeze({
  read_customers: 'Clienti (lettura)',
  read_orders: 'Ordini (lettura)',
  read_all_orders: 'Ordini, anche quelli vecchi (lettura)',
  read_products: 'Prodotti (lettura)',
  write_products: 'Prodotti (scrittura)',
  read_inventory: 'Giacenze (lettura)',
  write_inventory: 'Giacenze (scrittura)',
  read_locations: 'Sedi (lettura)',
  read_publications: 'Canali di vendita (lettura)',
  write_publications: 'Pubblicazione sui canali di vendita',
  read_fulfillments: 'Evasioni (lettura)',
  write_fulfillments: 'Evasioni (scrittura)',
  read_merchant_managed_fulfillment_orders: 'Evasioni del negozio (lettura)',
  write_merchant_managed_fulfillment_orders: 'Evasioni del negozio (scrittura)',
  read_returns: 'Resi (lettura)',
  write_discounts: 'Codici sconto (scrittura)',
  read_discounts: 'Codici sconto (lettura)',
  write_orders: 'Ordini (scrittura: tracking e note)',
  read_companies: 'Aziende clienti B2B (lettura)',
  read_customer_merge: 'Unione dei clienti (lettura)',
  read_gift_cards: 'Carte regalo (lettura)',
  read_markets: 'Mercati e valute (lettura)',
  read_own_subscription_contracts: 'Abbonamenti (lettura)',
  read_purchase_options: 'Opzioni di acquisto (lettura)',
});

export const scopeText = (scope: string): string => SCOPE_TEXT[scope] ?? scope.replace(/_/g, ' ');
